import { Bot, ClipboardPaste, FileText, GitCompare, Rocket, Search, Shield, Upload } from 'lucide-react';

import { stripLeadingSlash } from './skill-command-utils';
import type { NovaSkill } from '../queries/chats';

export interface SkillCommand {
  id: string;
  handle: string;
  description: string;
  overview: string;
  category: string;
  title?: string;
  exampleUsage?: string;
  instructionPlaceholder?: string;
  accountRequired?: boolean;
  skill?: NovaSkill;
}

export interface SkillCommandGroup {
  category: string;
  commands: SkillCommand[];
}

export type SkillSourceGroup = 'curated' | 'personal' | 'shared';

export interface SkillCommandSourceGroup {
  source: SkillSourceGroup;
  label: string;
  commands: SkillCommand[];
}

/**
 * Map skill categories to Lucide icons
 */
export const CATEGORY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  GET: Search,
  FILL: FileText,
  REQUEST: Upload,
  INGEST: ClipboardPaste,
  CREATE: FileText,
  COMPARE: GitCompare,
  AUDIT: Shield,
  NEW: Rocket,
  SKILL: Bot,
};

/**
 * Map skill categories to human-readable display names
 */
export const CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  GET: 'Get',
  FILL: 'Fill',
  REQUEST: 'Request',
  INGEST: 'Ingest',
  CREATE: 'Create',
  COMPARE: 'Compare',
  AUDIT: 'Audit',
  NEW: "What's New",
  SKILL: 'Skills',
};

export const OTHER_SKILL_CATEGORY = 'Other';
export const MY_SKILLS_CATEGORY = 'My Skills';
export const SHARED_WITH_YOU_SKILL_CATEGORY = 'Shared with you';

export const SKILL_SOURCE_DISPLAY_NAMES: Record<SkillSourceGroup, string> = {
  curated: 'WTW Official',
  personal: 'Your skills',
  shared: 'Shared skills',
};

const SKILL_SOURCE_ORDER: SkillSourceGroup[] = ['curated', 'personal', 'shared'];

export function getSkillCommandSource(command: SkillCommand): SkillSourceGroup {
  const sourceType = command.skill?.sourceDetails?.type;

  if (sourceType === 'PERSONAL') {
    return 'personal';
  }

  if (sourceType === 'SHARED') {
    return 'shared';
  }

  if (sourceType === 'CURATED') {
    return 'curated';
  }

  if (command.skill?.source === 'personal') {
    return 'personal';
  }

  if (command.skill?.source === 'shared') {
    return 'shared';
  }

  return 'curated';
}

export function sortSkillCommandsBySource(commands: SkillCommand[]): SkillCommand[] {
  return [...commands].sort((left, right) => {
    const sourceDifference =
      SKILL_SOURCE_ORDER.indexOf(getSkillCommandSource(left)) -
      SKILL_SOURCE_ORDER.indexOf(getSkillCommandSource(right));

    if (sourceDifference !== 0) {
      return sourceDifference;
    }

    const displayDifference = (left.title ?? left.handle).localeCompare(right.title ?? right.handle);
    return displayDifference || left.id.localeCompare(right.id);
  });
}

function getSkillCategory(skill: NovaSkill): string {
  if (skill.source === 'personal') {
    return MY_SKILLS_CATEGORY;
  }

  if (skill.source === 'shared') {
    return SHARED_WITH_YOU_SKILL_CATEGORY;
  }

  return skill.category?.trim() || OTHER_SKILL_CATEGORY;
}

function getFirstDisplayText(...values: Array<string | null | undefined>): string {
  for (const value of values) {
    const trimmedValue = value?.trim();
    if (trimmedValue) {
      return trimmedValue;
    }
  }

  return '';
}

export function buildSkillCommand(skill: NovaSkill): SkillCommand {
  // Only set title when it differs from the handle to avoid duplicate display
  const handle = stripLeadingSlash(skill.command);
  const title = skill.name !== handle ? skill.name : undefined;

  return {
    id: `skill:${skill.id}`,
    handle,
    title,
    description: getFirstDisplayText(skill.summary, skill.description),
    overview: getFirstDisplayText(skill.overview, skill.description),
    category: getSkillCategory(skill),
    exampleUsage: skill.exampleUsage ?? undefined,
    instructionPlaceholder: skill.instructionPlaceholder ?? undefined,
    accountRequired: skill.accountRequired ?? false,
    skill,
  };
}

export function buildSkillCommands(skills: NovaSkill[]): SkillCommand[] {
  return [...skills]
    .filter((skill) => skill.enabled)
    .map(buildSkillCommand)
    .sort((left, right) => {
      if (left.category !== right.category) {
        if (left.category === OTHER_SKILL_CATEGORY) {
          return 1;
        }
        if (right.category === OTHER_SKILL_CATEGORY) {
          return -1;
        }
        return left.category.localeCompare(right.category);
      }

      return (left.title ?? left.handle).localeCompare(right.title ?? right.handle);
    });
}

export function groupSkillCommandsByCategory(commands: SkillCommand[]): SkillCommandGroup[] {
  const groups = new Map<string, SkillCommand[]>();

  commands.forEach((command) => {
    const existingGroup = groups.get(command.category);
    if (existingGroup) {
      existingGroup.push(command);
      return;
    }

    groups.set(command.category, [command]);
  });

  return Array.from(groups.entries(), ([category, categoryCommands]) => ({
    category,
    commands: categoryCommands,
  }));
}

export function groupSkillCommandsBySource(commands: SkillCommand[]): SkillCommandSourceGroup[] {
  const commandsBySource = new Map<SkillSourceGroup, SkillCommand[]>();

  sortSkillCommandsBySource(commands).forEach((command) => {
    const source = getSkillCommandSource(command);
    const sourceCommands = commandsBySource.get(source);
    if (sourceCommands) {
      sourceCommands.push(command);
      return;
    }

    commandsBySource.set(source, [command]);
  });

  return SKILL_SOURCE_ORDER.flatMap((source) => {
    const sourceCommands = commandsBySource.get(source);
    return sourceCommands ? [{ source, label: SKILL_SOURCE_DISPLAY_NAMES[source], commands: sourceCommands }] : [];
  });
}

export function getSkillCommandIcon(command: SkillCommand): React.ComponentType<{ className?: string }> {
  return CATEGORY_ICONS[command.category] || CATEGORY_ICONS.SKILL || FileText;
}

export function isCustomSkillCommand(command: SkillCommand): command is SkillCommand & { skill: NovaSkill } {
  return Boolean(command.skill) && command.skill?.source === 'personal';
}
